/**
 * Die Persona-Sets, die dieses Konto benutzen kann.
 *
 * EIGENE UND GETEILTE: Die Liste kommt aus `listSets` — derselben Funktion, die
 * auch der MCP benutzt. Sie ruft `sim_persona_sets_visible_to` und liefert
 * geteilte Marktsets PLUS die eigenen privaten. Hier keine eigene Abfrage zu
 * schreiben ist der ganze Punkt: sonst gäbe es zwei Wahrheiten darüber, welches
 * Set ein Konto sehen darf, und `evidence_backed` wäre zweimal definiert.
 *
 * WARUM DIE BELEGZAHL SO PROMINENT STEHT: Sie ist der Unterschied zwischen
 * einem Set, das eine Aussage trägt, und einem, das plausibel klingt. Ein
 * Hypothesen-Set ist zulässig — es soll nur niemand für etwas anderes halten.
 */

import { EINRICHTUNG } from '../preise';
import { useCallback, useEffect, useState } from 'react';
import { ApiFehler, nochmalSinnvoll, ladeModelle, ladeSets, type Modellkarte, type PersonaSet } from '../api';
import { useSitzung } from '../session';
import { Abschnitt, AppRahmen, Balken, Hinweis, KleinerKnopf, Kopierzeile, Marke, Panel, Platzhalter, Seitenkopf, Verweis, Zeile } from '../ui';
import { useNeuLadenBeimZurueckkehren } from '../neu-laden';
import { beleglageAusZaehlung, beleglageZeile } from '../beleglage';
import { locale, sprache, t } from '../sprache';

export default function Personas() {
  const { session, abmelden } = useSitzung();
  const [sets, setSets] = useState<PersonaSet[] | null>(null);
  const [modelle, setModelle] = useState<Modellkarte[]>([]);
  const [fehler, setFehler] = useState('');
  const [nochmal, setNochmal] = useState(true);
  const [laedt, setLaedt] = useState(true);

  const holen = useCallback(async () => {
    setLaedt(true);
    setFehler('');
    try {
      setSets(await ladeSets());
      // Modelle sind eine Beigabe: fehlt die Route (noch nicht ausgerollt),
      // bleibt die Seite trotzdem benutzbar.
      setModelle(await ladeModelle().catch(e => { console.warn('[personas] Modelle', e); return []; }));
    } catch (e) {
      const f = e as ApiFehler;
      setFehler(f.message);
      // Gesperrtes Konto: ein zweiter Versuch aendert nichts, also kein Knopf dafuer.
      setNochmal(nochmalSinnvoll(f));
      if (f.abgemeldet) void abmelden();
    } finally {
      setLaedt(false);
    }
  }, [abmelden]);

  useEffect(() => { void holen(); }, [holen]);
  // Sets entstehen in Claude: beim Zurueckkehren in den Tab neu laden (neu-laden.ts).
  useNeuLadenBeimZurueckkehren(() => void holen());

  const eigene = sets?.filter(s => s.visibility !== 'shared') ?? [];
  const geteilte = sets?.filter(s => s.visibility === 'shared') ?? [];

  return (
    <AppRahmen email={session?.user?.email ?? ''} abmelden={() => void abmelden()}>
      {/* „Zielgruppen", nicht „Personas" (03.10.2026): so heisst es seit dem
          29.09. in Claude, auf der Website und im Rest der App (Wurzel-
          AGENTS.md, „Zielgruppe, nicht Persona-Set"). Die Route bleibt /personas. */}
      <Seitenkopf
        titel={t('Zielgruppen', 'Audiences')}
        unter={t('Gegen diese Zielgruppen prüfen deine Runs. Deine eigene trifft dein Angebot, eine fertige ist ein Beispiel.', 'Your runs test against these audiences. Your own fits your offer, a ready-made one is an example.')}
      />

      {fehler && <Hinweis art="schlecht">{fehler}{nochmal && <> <button type="button" onClick={() => void holen()} className="cursor-pointer border-0 bg-transparent p-0 font-medium text-current underline underline-offset-[3px]">{t('Noch einmal laden', 'Load again')}</button></>}</Hinweis>}

      <Abschnitt titel={t('Deine Zielgruppen', 'Your audiences')} zahl={sets ? eigene.length : undefined}>
        {laedt && !sets && (
          <Panel>
            {[0, 1].map(i => <Zeile key={i}><Platzhalter zeilen={3} /></Zeile>)}
          </Panel>
        )}
        {!laedt && !sets && fehler && (
          <Panel className="px-4 py-5 sm:px-5">
            <p className="m-0 text-[0.9375rem] leading-relaxed text-text-leise">
              {t('Die Zielgruppen ließen sich nicht laden.', 'The audiences could not be loaded.')}
            </p>
            {nochmal && <KleinerKnopf className="mt-3" onClick={() => void holen()} disabled={laedt}>{laedt ? t('Lädt …', 'Loading …') : t('Noch einmal laden', 'Load again')}</KleinerKnopf>}
          </Panel>
        )}
        {sets && eigene.length === 0 && (
          <Panel className="px-4 py-5 sm:px-6 sm:py-6">
            <p className="mt-0 mb-1 text-[0.9375rem] font-semibold text-tinte">{t('Noch keine eigene Zielgruppe', 'No audience of your own yet')}</p>
            <p className="mt-0 mb-4 max-w-[62ch] text-[0.9375rem] leading-relaxed text-text-leise">
              {t(
                <>Sag das in Claude. Es führt durch Intake, Belegsuche und Entwurf;
                beim Speichern kostet die Zielgruppe einmalig {EINRICHTUNG} Credits:</>,
                <>Say this in Claude. It guides you through intake, evidence search and draft.
                Saving the audience costs a one-time {EINRICHTUNG} credits:</>,
              )}
            </p>
            <Kopierzeile art="text" text={t('Lass uns Personas für mein Angebot bauen: [Produkt, Zielgruppe, Markt].', 'Let’s build personas for my offer: [product, audience, market].')} />
            <p className="mt-3 mb-0 text-[0.8125rem] leading-relaxed text-text-hell">
              {geteilte.length > 0 && t('Bis dahin kannst du gegen eine fertige Zielgruppe unten prüfen. ', 'Until then you can test against a ready-made audience below. ')}{t('Mehr unter', 'More in the')}{' '}
              <Verweis to="/guide#personas">{t('Anleitung', 'guide')}</Verweis>.
            </p>
          </Panel>
        )}
        {eigene.length > 0 && (
          <>
            <Panel>{eigene.map(s => <SetZeile key={s.slug} s={s} />)}</Panel>
            {/* Fuer die zweite Marke oder ein neues Angebot: derselbe Satz wie im
                leeren Zustand, nur leiser. */}
            <details className="group mt-3 [&_summary::-webkit-details-marker]:hidden">
              <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 text-[0.875rem] font-medium text-tinte hover:text-marke-tief">
                <span aria-hidden className="inline-block text-text-hell transition-transform duration-200 group-open:rotate-90">▸</span>
                {t('Weitere Zielgruppe aufbauen', 'Build another audience')}
              </summary>
              <div className="mt-2.5 max-w-[44rem]">
                <Kopierzeile art="text" text={t('Lass uns Personas für mein Angebot bauen: [Produkt, Zielgruppe, Markt].', 'Let’s build personas for my offer: [product, audience, market].')} />
                <p className="mt-2 mb-0 text-[0.8125rem] text-text-hell">{t(<>In Claude. Beim Speichern einmalig {EINRICHTUNG} Credits, Änderungen danach kostenlos.</>, <>In Claude. A one-time {EINRICHTUNG} credits when saving, changes after that are free.</>)}</p>
              </div>
            </details>
          </>
        )}
      </Abschnitt>

      {geteilte.length > 0 && (
        <Abschnitt
          titel={t('Fertige Zielgruppen', 'Ready-made audiences')}
          zahl={geteilte.length}
          unter={t(<>
            Recherchiert und belegt, zum Ausprobieren oder für einen schnellen ersten Eindruck.
            Ein Run dagegen beantwortet die Frage dieser Zielgruppe, nicht deiner; für eine
            belastbare Aussage über deine Kunden brauchst du eine eigene.
          </>, <>
            Researched and backed by evidence, for trying things out or a quick first impression.
            A run against one answers the question of that audience, not yours. For a
            reliable statement about your customers you need your own.
          </>)}
        >
          <Panel>{geteilte.map(s => <SetZeile key={s.slug} s={s} />)}</Panel>
        </Abschnitt>
      )}
      {modelle.length > 0 && (
        <Abschnitt
          titel={t('Simulationsmodelle', 'Simulation models')}
          zahl={modelle.length}
          unter={t(<>
            Eine feste Jury aus einer fertigen Zielgruppe: kuratiert, in einer festen Fassung, läuft auf Claude.
            Kein eigenes Sprachmodell. Was ein Modell trägt, ist seine Trefferquote, und die steht
            erst ab zehn gemeldeten Marktergebnissen.
          </>, <>
            A fixed jury from a ready-made audience: curated, in a fixed version, runs on Claude.
            Not a language model of its own. What a model stands on is its hit rate, and that appears
            only after ten reported market results.
          </>)}
        >
          <Panel>{modelle.map(m => <ModellZeile key={m.slug} m={m} />)}</Panel>
        </Abschnitt>
      )}

    </AppRahmen>
  );
}

/**
 * Die Art der Zielgruppe als ruhiges Etikett. Sie bestimmt, in welcher Rolle
 * die Jury liest (Firmenkunde, Privatperson, Waehlerin). Ohne Farbe: sie ist
 * eine Eigenschaft, kein Zustand wie „Belegt".
 */
const ZIELGRUPPE: Record<NonNullable<PersonaSet['zielgruppe_art']>, string> = {
  b2b: 'Firmenkunden',
  b2c: 'Privatkunden',
  politik: 'Politik',
};
const ZIELGRUPPE_EN: typeof ZIELGRUPPE = {
  b2b: 'Business customers',
  b2c: 'Consumers',
  politik: 'Politics',
};

/**
 * Ein Set als Zeile: links, was es ist, rechts die Beleglage. So lassen sich
 * Sets untereinander vergleichen, statt Karte fuer Karte zu lesen.
 */
function SetZeile({ s }: { s: PersonaSet }) {
  const ohneBeleg = s.persona_count - s.evidence_persona_count;
  const herkunft = [s.market, s.segment, s.language].filter(Boolean).join(' · ');
  const quellen = beleglageZeile(beleglageAusZaehlung(s.evidence_count, s.evidence_by_type));
  return (
    <div className="grid gap-x-8 gap-y-4 border-t border-linie-zart px-4 py-4 first:border-t-0 sm:px-5 md:grid-cols-[minmax(0,1fr)_14rem]">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <span className="text-[0.9375rem] font-semibold text-tinte">{s.name}</span>
          {s.zielgruppe_art && <Marke>{(sprache() === 'en' ? ZIELGRUPPE_EN : ZIELGRUPPE)[s.zielgruppe_art]}</Marke>}
          {/* „Ohne Belege" neben „Mit Belegen 5 von 7" widersprach sich. */}
          {s.evidence_backed ? <Marke art="gut">{t('Belegt', 'Backed')}</Marke> : s.evidence_persona_count > 0 ? <Marke>{t('Teilweise belegt', 'Partly backed')}</Marke> : <Marke>{t('Ohne Belege', 'No evidence')}</Marke>}
        </div>
        <code className="font-mono text-[0.8125rem] break-all text-text-hell">{s.slug}</code>
        {s.description && (
          <p className="mt-2 mb-0 max-w-[62ch] text-[0.875rem] leading-relaxed text-text-leise">{s.description}</p>
        )}
        <div className="mt-1.5 text-[0.8125rem] text-text-hell tabular-nums">
          {herkunft && <>{herkunft} · </>}{t(`${s.persona_count} Personas`, `${s.persona_count} ${s.persona_count === 1 ? 'persona' : 'personas'}`)}
        </div>
      </div>
      <div className="md:pt-0.5">
        <div className="mb-1.5 flex justify-between gap-3 text-[0.8125rem] tabular-nums">
          <span className="text-text-leise">{t('Mit Belegen', 'With evidence')}</span>
          <span className="font-medium text-text">{t(`${s.evidence_persona_count} von ${s.persona_count}`, `${s.evidence_persona_count} of ${s.persona_count}`)}</span>
        </div>
        <Balken wert={s.evidence_persona_count} von={s.persona_count} label={t('Personas mit Belegen', 'Personas with evidence')} />
        {quellen && <p className="mt-2 mb-0 text-[0.8125rem] leading-relaxed text-text-leise tabular-nums">{quellen}</p>}
        <p className="mt-2 mb-0 text-[0.8125rem] leading-relaxed text-text-hell">
          {s.evidence_backed
            ? t('Jede Persona trägt mindestens eine Quelle, Einwände nennen sie.', 'Every persona has at least one source, and objections cite it.')
            : t(
              <>{ohneBeleg} von {s.persona_count} Personas ohne Belege. Ihre Einwände kommen ohne Quellenangabe, und das steht in jeder Antwort.</>,
              <>{ohneBeleg} of {s.persona_count} personas without evidence. Their objections come without a source, and every answer says so.</>,
            )}
        </p>
      </div>
    </div>
  );
}

const prozent = (v: number) => {
  const z = new Intl.NumberFormat(locale(), { maximumFractionDigits: 1 }).format(v * 100);
  return t(`${z} %`, `${z}%`);
};

function ModellZeile({ m }: { m: Modellkarte }) {
  const q = m.trefferquote;
  return (
    <div className="grid gap-x-8 gap-y-4 border-t border-linie-zart px-4 py-4 first:border-t-0 sm:px-5 md:grid-cols-[minmax(0,1fr)_14rem]">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <span className="text-[0.9375rem] font-semibold text-tinte">{m.name}</span>
          {q.gemessen
            ? <Marke art={q.besser_als_zufall ? 'gut' : 'neutral'}>{q.besser_als_zufall ? t('Besser als Zufall', 'Better than chance') : t('Zwischenstand', 'Interim result')}</Marke>
            : <Marke>{t('Noch nicht gemessen', 'Not measured yet')}</Marke>}
        </div>
        <code className="font-mono text-[0.8125rem] break-all text-text-hell">{m.slug}</code>
        <p className="mt-2 mb-0 max-w-[62ch] text-[0.875rem] leading-relaxed text-text-leise">{m.kurz}</p>
        <div className="mt-1.5 text-[0.8125rem] text-text-hell tabular-nums">
          {t(`${m.personas} Personas`, `${m.personas} ${m.personas === 1 ? 'persona' : 'personas'}`)} · {m.fassung ? t(`Fassung ${m.fassung}`, `version ${m.fassung}`) : t('folgt der Zielgruppe', 'follows the audience')} · {t('läuft auf', 'runs on')} {m.sprachmodell}
        </div>
        <div className="mt-3 max-w-[34rem]">
          <Kopierzeile art="text" text={t(`Prüf meinen Text mit dem Modell ${m.slug}.`, `Check my copy with the model ${m.slug}.`)} />
        </div>
      </div>
      <div className="md:pt-0.5">
        <div className="mb-1.5 flex justify-between gap-3 text-[0.8125rem] tabular-nums">
          <span className="text-text-leise">{t('Trefferquote', 'Hit rate')}</span>
          <span className="font-medium text-text">
            {q.gemessen && q.quote != null && q.intervall
              ? <>{prozent(q.quote)} · {t(`${q.treffer} von ${q.n}`, `${q.treffer} of ${q.n}`)}</>
              : <>{t(`${q.n} von 10 Ergebnissen`, `${q.n} of 10 results`)}</>}
          </span>
        </div>
        {!q.gemessen && <Balken wert={q.n} von={10} label={t('Gemeldete Ergebnisse bis zur ersten Quote', 'Reported results until the first hit rate')} />}
        <p className="mt-2 mb-0 text-[0.8125rem] leading-relaxed text-text-hell">{q.satz}</p>
      </div>
    </div>
  );
}
